import { User } from "@shared/types/user";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Conversation } from "../types";

export interface ChatWindow {
  conversationId: string;
  conversationType: "direct" | "group";
  title: string;
  user?: User;
  group?: Conversation;
  isMinimized: boolean;
  unreadCount?: number;
  openedAt?: number;
}

interface ChitChatContextType {
  isDropdownOpen: boolean;
  openDropdown: () => void;
  closeDropdown: () => void;
  toggleDropdown: () => void;
  chatWindows: ChatWindow[];
  openChatWindow: (window: Omit<ChatWindow, "isMinimized">) => void;
  closeChatWindow: (conversationId: string) => void;
  minimizeChatWindow: (conversationId: string) => void;
  maximizeChatWindow: (conversationId: string) => void;
  getOpenWindow: () => ChatWindow | null;
  minimizeAllWindows: () => void;
  updateUnreadCount: (conversationId: string, count: number) => void;
  getTotalUnreadCount: () => number;
}

const ChitChatContext = createContext<ChitChatContextType | null>(null);

const STORAGE_KEY = "chitchat_windows";

export const ChitChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [chatWindows, setChatWindows] = useState<ChatWindow[]>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.map((w: ChatWindow) => ({ ...w, isMinimized: true }));
      }
    } catch {
      // Ignore parse errors
    }
    return [];
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(chatWindows));
      window.dispatchEvent(
        new CustomEvent("chatWindowsChanged", {
          detail: { chatWindows },
        }),
      );
    } catch {
      // Ignore storage errors
    }
  }, [chatWindows]);

  const openDropdown = useCallback(() => {
    setIsDropdownOpen(true);
  }, []);

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const openChatWindow = useCallback(
    (window: Omit<ChatWindow, "isMinimized">) => {
      setChatWindows((prev) => {
        const existing = prev.find(
          (w) => w.conversationId === window.conversationId,
        );
        if (existing) {
          return prev.map((w) =>
            w.conversationId === window.conversationId
              ? {
                  ...w,
                  isMinimized: false,
                  openedAt: Date.now(),
                  title:
                    window.title && window.title !== "Chat"
                      ? window.title
                      : w.title,
                  user: window.user || w.user,
                }
              : { ...w, isMinimized: true },
          );
        }
        const minimizedPrev = prev.map((w) => ({ ...w, isMinimized: true }));
        return [
          ...minimizedPrev,
          { ...window, isMinimized: false, openedAt: Date.now() },
        ];
      });
      setIsDropdownOpen(false);
    },
    [],
  );

  const closeChatWindow = useCallback((conversationId: string) => {
    setChatWindows((prev) =>
      prev.filter((w) => w.conversationId !== conversationId),
    );
  }, []);

  const minimizeChatWindow = useCallback((conversationId: string) => {
    setChatWindows((prev) =>
      prev.map((w) =>
        w.conversationId === conversationId ? { ...w, isMinimized: true } : w,
      ),
    );
  }, []);

  const maximizeChatWindow = useCallback((conversationId: string) => {
    setChatWindows((prev) =>
      prev.map((w) =>
        w.conversationId === conversationId
          ? { ...w, isMinimized: false, openedAt: Date.now() }
          : { ...w, isMinimized: true },
      ),
    );
  }, []);

  const getOpenWindow = useCallback(() => {
    return chatWindows.find((w) => !w.isMinimized) || null;
  }, [chatWindows]);

  const minimizeAllWindows = useCallback(() => {
    setChatWindows((prev) => prev.map((w) => ({ ...w, isMinimized: true })));
  }, []);

  const updateUnreadCount = useCallback(
    (conversationId: string, count: number) => {
      setChatWindows((prev) =>
        prev.map((w) =>
          w.conversationId === conversationId
            ? { ...w, unreadCount: count }
            : w,
        ),
      );
    },
    [],
  );

  const getTotalUnreadCount = useCallback(() => {
    return chatWindows.reduce((total, w) => total + (w.unreadCount || 0), 0);
  }, [chatWindows]);

  return (
    <ChitChatContext.Provider
      value={{
        isDropdownOpen,
        openDropdown,
        closeDropdown,
        toggleDropdown,
        chatWindows,
        openChatWindow,
        closeChatWindow,
        minimizeChatWindow,
        maximizeChatWindow,
        getOpenWindow,
        minimizeAllWindows,
        updateUnreadCount,
        getTotalUnreadCount,
      }}
    >
      {children}
    </ChitChatContext.Provider>
  );
};

export const useChitChat = (): ChitChatContextType => {
  const context = useContext(ChitChatContext);
  if (!context) {
    throw new Error("useChitChat must be used within a ChitChatProvider");
  }
  return context;
};
